import React, { useState, useEffect, useCallback } from 'react';
import PdfAnnotationViewer from '../../../ritningar/components/PdfAnnotationViewer';
import { API_URL } from '../../../../utils/api';
import useBackdropClose from '../../../../hooks/useBackdropClose';

/**
 * SkissModal - Visar BK-skiss-PDF med annoteringsmöjlighet.
 * Designad i samma stil som Ritningar-modulen: mörkgrå header med Tillbaka-knapp
 * + blå instruktionsbanner + stor PDF-vy.
 */
const SkissModal = ({ file, procurementId, currentUser, onClose }) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [reviewId, setReviewId] = useState(null);
  const [sending, setSending] = useState(false);
  const [sentAt, setSentAt] = useState(null);
  // Ögonblicksbilder av vad som sparats respektive skickats, så knapparna kan
  // gråas ut när det inte finns något nytt att göra.
  const [sparadVersion, setSparadVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  // Hämta PDF + befintliga annotations
  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');

        // PDF som blob
        const pdfRes = await fetch(`${API_URL}/ia-ritningar/file/${file.driveId}/${file.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const blob = await pdfRes.blob();
        setPdfBlobUrl(URL.createObjectURL(blob));

        // Befintlig granskning (kräver giltigt procurementId)
        if (procurementId) {
          const reviewRes = await fetch(
            `${API_URL}/api/estimat-skiss/procurement/${procurementId}/file/${encodeURIComponent(file.id)}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (reviewRes.ok) {
            const review = await reviewRes.json();
            if (review) {
              setReviewId(review._id);
              setAnnotations(review.annotations || []);
              setSparadVersion(JSON.stringify(review.annotations || []));
            }
          }
        }
      } catch (err) {
        console.error('Fel vid laddning av skiss:', err);
        setError('Kunde inte ladda skissen');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [file, procurementId]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat-skiss/procurement/${procurementId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileId: file.id,
          fileName: file.name,
          annotations
        })
      });
      if (!res.ok) throw new Error('Kunde inte spara');
      const updated = await res.json();
      setReviewId(updated._id);
      setAnnotations(updated.annotations || []);
      setSparadVersion(JSON.stringify(updated.annotations || []));
      setSavedAt(new Date());
      return updated._id;
    } catch (err) {
      console.error('Fel vid sparande:', err);
      setError('Kunde inte spara dina kommentarer');
    } finally {
      setSaving(false);
    }
  }, [procurementId, file, annotations]);

  /**
   * Skicka kommentarerna till motparten. Sparningen är tyst — utan den
   * uppdelningen hade varje sparad markering gett en egen notis.
   * Sparar först, så inget osparat missas i utskicket.
   */
  const handleSend = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      const aktivtId = reviewId || await handleSave();
      if (!aktivtId) throw new Error('Inget att skicka');

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat-skiss/${aktivtId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Kunde inte skicka');

      // Uppdatera vyn med serverns version — där är utkastflaggorna rensade,
      // så Skicka-knappen gråas ut direkt.
      const svar = await res.json().catch(() => ({}));
      if (svar.review) {
        setAnnotations(svar.review.annotations || []);
        setSparadVersion(JSON.stringify(svar.review.annotations || []));
      }
      setSentAt(new Date());
    } catch (err) {
      console.error('Fel vid skickande:', err);
      setError('Kunde inte skicka kommentarerna');
    } finally {
      setSending(false);
    }
  }, [reviewId, handleSave]);

  const handleReply = useCallback(async (annotationId, text) => {
    if (!reviewId) {
      await handleSave();
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat-skiss/${reviewId}/reply`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ annotationId, text })
      });
      if (!res.ok) throw new Error('Kunde inte svara');
      const updated = await res.json();
      setAnnotations(updated.annotations || []);
      setSparadVersion(JSON.stringify(updated.annotations || []));
    } catch (err) {
      console.error('Fel vid svar:', err);
    }
  }, [reviewId, handleSave]);

  const backdrop = useBackdropClose(onClose);

  // Osparade ändringar respektive oskickade ändringar — styr om knapparna är
  // aktiva, så det syns direkt vad som återstår att göra.
  const nuvarandeVersion = JSON.stringify(annotations);
  const harOsparat = nuvarandeVersion !== sparadVersion;
  // Egna utkast är det enda som återstår att skicka. Motpartens utkast filtreras
  // bort av servern, så de kan aldrig göra knappen aktiv av misstag.
  const harEgnaUtkast = annotations.some(a =>
    (a.comments || []).some(c => c.draft && String(c.authorId) === String(currentUser?._id))
  );
  const harOskickat = harOsparat || harEgnaUtkast;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4" {...backdrop}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full h-full max-w-[1600px] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mörkgrå header med Tillbaka-knapp + filnamn + ev. sparat-indikator */}
        <div className="bg-leadme-gray-dark px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white text-sm flex items-center mb-1"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Tillbaka
            </button>
            <h2 className="text-xl font-bold text-white">{file.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            {sentAt ? (
              <span className="text-xs text-green-300">
                Skickat {sentAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : savedAt && (
              <span className="text-xs text-green-300">Sparat {savedAt.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</span>
            )}
            {error && <span className="text-xs text-red-300">{error}</span>}
            <button
              onClick={handleSave}
              disabled={saving || loading || !harOsparat}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                harOsparat && !saving && !loading
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
              title={harOsparat ? 'Sparar utan att meddela någon' : 'Allt är sparat'}
            >
              {saving ? 'Sparar...' : 'Spara kommentarer'}
            </button>
            <button
              onClick={handleSend}
              disabled={sending || saving || loading || !harOskickat}
              className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                harOskickat && !sending && !saving && !loading
                  ? 'bg-leadme-pink text-white hover:bg-leadme-pink-dark'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              }`}
              title={
                annotations.length === 0
                  ? 'Inga kommentarer att skicka'
                  : harOskickat ? 'Sparar och meddelar motparten' : 'Allt är redan skickat'
              }
            >
              {sending ? 'Skickar...' : 'Skicka kommentarer'}
            </button>
          </div>
        </div>

        {/* Blå instruktionsbanner */}
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-4 text-xs text-blue-700 flex-wrap">
          <span className="flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <strong>Textmarkering:</strong> Markera text direkt i PDF:en för att kommentera
          </span>
          <span className="text-blue-300 hidden sm:inline">|</span>
          <span>
            <strong>Områdesmarkering:</strong> Tryck på <strong>Markera område</strong> och dra, eller håll <kbd className="bg-blue-100 border border-blue-300 rounded px-1 py-0.5 text-[10px] font-mono">Alt</kbd> (<kbd className="bg-blue-100 border border-blue-300 rounded px-1 py-0.5 text-[10px] font-mono">⌥ Option</kbd> på Mac) + klicka och dra
          </span>
          <span className="text-blue-300 hidden sm:inline">|</span>
          <span>
            <strong>Klar?</strong> "Spara kommentarer" sparar utan att meddela någon — "Skicka kommentarer" meddelar motparten
          </span>
        </div>

        {/* PDF-vy */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-leadme-pink"></div>
            </div>
          ) : pdfBlobUrl ? (
            <PdfAnnotationViewer
              pdfUrl={pdfBlobUrl}
              initialAnnotations={annotations}
              authorRole={currentUser?.role || 'kund'}
              currentUser={currentUser}
              canReply={true}
              onAnnotationsChange={setAnnotations}
              onReply={handleReply}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Kunde inte ladda PDF
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkissModal;
